import React, { useState } from 'react';
import {
  useGetSuggestionPerformanceQuery,
  useGetEventInterestQuery,
  type SuggestionRates,
  type SuggestionConfidenceRow,
} from '../../redux/analytics/analytics.api';
import type { SuggestionKind } from '../../redux/settings/settings.api';

const PERFORMANCE_WINDOWS = [7, 14, 30, 60, 90];
const ATTRIBUTION_WINDOWS = [1, 3, 7, 14, 30];
const INTEREST_WINDOWS = [7, 30, 90, 180, 365];

const KIND_LABEL: Record<SuggestionKind, string> = {
  new_event: 'New event',
  starting_soon: 'Starting soon',
};

const CONFIDENCE_LABEL: Record<string, string> = {
  exact: 'Exact match',
  bridge: 'Bridged match',
};

/** Counts with thousands separators. Never renders `NaN`. */
const num = (value: number): string =>
  Number.isFinite(value) ? Math.round(value).toLocaleString() : '0';

/**
 * A [0,1] fraction from the API rendered as a percentage.
 *
 * `denominator` is the count the rate is taken over. The backend already
 * promises 0 rather than NaN, but a single `NaN%` in an admin panel destroys
 * trust in every other number on the page, so the guard is repeated here. With
 * a zero denominator there is no rate to report at all — that reads as an
 * em dash, not as a real `0.0%`, which would look like a measured failure.
 */
const pct = (value: number, denominator: number): string =>
  denominator > 0 && Number.isFinite(value)
    ? `${(value * 100).toFixed(1)}%`
    : '—';

/** Share of a whole, computed here rather than taken from the API. */
const shareOf = (part: number, whole: number): string =>
  whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : '—';

/** Date only: these windows are days wide, so the clock time is noise. */
const day = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime())
    ? 'an unknown date'
    : parsed.toLocaleDateString();
};

interface DaySelectProps {
  label: string;
  value: number;
  options: number[];
  onChange: (days: number) => void;
  ariaLabel: string;
}

const DaySelect = ({
  label,
  value,
  options,
  onChange,
  ariaLabel,
}: DaySelectProps): React.JSX.Element => (
  <label className="app-settings__field" style={{ marginTop: 0 }}>
    <span className="app-settings__field-label">{label}</span>
    <select
      className="admin-events__status"
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      aria-label={ariaLabel}
    >
      {options.map((days) => (
        <option key={days} value={days}>
          {days === 1 ? '1 day' : `${days} days`}
        </option>
      ))}
    </select>
  </label>
);

const StatTile = ({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}): React.JSX.Element => (
  <div className="user-detail__card">
    <span className="app-settings__field-label">{label}</span>
    <span className="font-grandstander text-2xl font-extrabold tabular-nums">
      {value}
    </span>
    {sub != null && (
      <span className="app-settings__hint" style={{ margin: 0 }}>
        {sub}
      </span>
    )}
  </div>
);

/** The seven cells every performance breakdown shares, in a fixed order. */
const RateCells = ({ row }: { row: SuggestionRates }): React.JSX.Element => (
  <>
    <td>{num(row.sent)}</td>
    <td>{num(row.delivered)}</td>
    <td>{num(row.failed)}</td>
    <td>{num(row.opened)}</td>
    <td>{num(row.joined)}</td>
    <td>{pct(row.openRate, row.sent)}</td>
    <td>{pct(row.joinRate, row.sent)}</td>
  </>
);

/**
 * One match tier, as a tile rather than a table row.
 *
 * `exact` against `bridge` is the number that decides whether the
 * category-bridge tier is pulling its weight or just adding noise, so it gets
 * visual weight instead of being buried in a breakdown table.
 */
const ConfidenceCard = ({
  row,
  totalSent,
}: {
  row: SuggestionConfidenceRow;
  totalSent: number;
}): React.JSX.Element => (
  <div className="user-detail__card">
    <h4 className="user-detail__card-title">
      {CONFIDENCE_LABEL[row.matchConfidence] ?? row.matchConfidence}
    </h4>
    <div className="app-settings__grid">
      <div>
        <span className="app-settings__field-label">Open rate</span>
        <div className="font-grandstander text-3xl font-extrabold tabular-nums">
          {pct(row.openRate, row.sent)}
        </div>
      </div>
      <div>
        <span className="app-settings__field-label">Join rate</span>
        <div className="font-grandstander text-3xl font-extrabold tabular-nums">
          {pct(row.joinRate, row.sent)}
        </div>
      </div>
    </div>
    <p className="app-settings__hint" style={{ margin: '10px 0 0' }}>
      <strong>{num(row.sent)}</strong> sent ({shareOf(row.sent, totalSent)} of
      all sends) · {num(row.delivered)} delivered · {num(row.failed)} failed ·{' '}
      {num(row.opened)} opened · {num(row.joined)} joined
    </p>
  </div>
);

/**
 * Reporting for the interest-based event suggestion push.
 *
 * Two sections that measure different things and must not be read as one:
 * the send log (what we suggested and what came of it) and the participation
 * log (what people joined, however they found it). Both tables are new and
 * normally empty, so every branch here has an explicit empty state — a card
 * rendering as `0 / NaN%` reads as a broken page, not as "no data yet".
 */
export const AdminAnalytics = (): React.JSX.Element => {
  const [days, setDays] = useState(30);
  const [attributionDays, setAttributionDays] = useState(7);
  const [interestDays, setInterestDays] = useState(90);

  const performance = useGetSuggestionPerformanceQuery({ days, attributionDays });
  const interest = useGetEventInterestQuery({ days: interestDays });

  const perf = performance.data;
  const totals = perf?.totals;
  const interestData = interest.data;
  const coverage = interestData?.coverage;

  const refresh = (): void => {
    void performance.refetch();
    void interest.refetch();
  };

  return (
    <div className="admin-users">
      <div className="admin-users__header">
        <div className="admin-users__title">
          <h2>Analytics</h2>
          <span className="admin-users__count">
            interest-based event suggestions
          </span>
        </div>
        <button
          type="button"
          className="admin-users__page-btn"
          onClick={refresh}
          disabled={performance.isFetching || interest.isFetching}
        >
          {performance.isFetching || interest.isFetching
            ? 'Refreshing…'
            : 'Refresh'}
        </button>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Section 1 — the send log                                          */}
      {/* ---------------------------------------------------------------- */}
      <section className="user-detail__card user-detail__card--wide">
        <div className="event-detail__section-head">
          <h3 className="user-detail__card-title" style={{ margin: 0 }}>
            Suggestion push performance
          </h3>
          <div className="admin-events__filters">
            <DaySelect
              label="Window"
              value={days}
              options={PERFORMANCE_WINDOWS}
              onChange={setDays}
              ariaLabel="Performance window in days"
            />
            <DaySelect
              label="Attribution"
              value={attributionDays}
              options={ATTRIBUTION_WINDOWS}
              onChange={setAttributionDays}
              ariaLabel="Attribution window in days"
            />
          </div>
        </div>

        <p className="app-settings__hint">
          Reads the <strong>send log</strong>: one row per (user, event) pair we
          pushed in the last <strong>{days}</strong> days. An open or a join
          counts towards a push only when it happens within{' '}
          <strong>{attributionDays === 1 ? '1 day' : `${attributionDays} days`}</strong>{' '}
          of it. Everything here is limited to events we <em>chose to suggest</em>.
        </p>

        {performance.isLoading && (
          <div className="admin-users__loading">Loading push performance…</div>
        )}
        {performance.error != null && (
          <div className="admin-users__error">
            Could not load suggestion push performance.
          </div>
        )}

        {totals != null && totals.sent === 0 && (
          <div className="admin-tab-empty" style={{ padding: '40px 24px' }}>
            <h2 className="admin-tab-empty__title">No pushes sent yet</h2>
            <p className="admin-tab-empty__sub">
              Nothing went out in the last {days} days, so there is nothing to
              measure. This is the expected reading before the feature is
              switched on: the send log only fills once{' '}
              <strong>Interest-based event suggestions</strong> is enabled on the
              App tab, or after a manual <strong>Send now</strong> there. A
              dry-run preview writes no rows at all.
            </p>
          </div>
        )}

        {perf != null && totals != null && totals.sent > 0 && (
          <>
            <div className="app-settings__grid">
              <StatTile label="Sent" value={num(totals.sent)} />
              <StatTile
                label="Delivered"
                value={num(totals.delivered)}
                sub={`${shareOf(totals.delivered, totals.sent)} of sent`}
              />
              <StatTile
                label="Failed"
                value={num(totals.failed)}
                sub={`${shareOf(totals.failed, totals.sent)} of sent`}
              />
              <StatTile label="Opened" value={num(totals.opened)} />
              <StatTile label="Joined" value={num(totals.joined)} />
              <StatTile
                label="Open rate"
                value={pct(totals.openRate, totals.sent)}
                sub={`of ${num(totals.sent)} sent`}
              />
              <StatTile
                label="Join rate"
                value={pct(totals.joinRate, totals.sent)}
                sub={`of ${num(totals.sent)} sent`}
              />
            </div>

            <h4 className="user-detail__card-title" style={{ marginTop: 24 }}>
              Exact vs bridged matches
            </h4>
            <p className="app-settings__hint">
              <strong>Exact</strong> means the event shares an activity id with
              the interests the user picked. <strong>Bridged</strong> means only
              the event&apos;s category overlapped them. The bridge tier exists
              to reach users no tagged event matches — if it carries a large
              share of the sends and converts far worse than exact, it is adding
              noise, and the bridged score floor on the App tab should go up.
            </p>
            <div className="user-detail__grid">
              {perf.byConfidence.map((row) => (
                <ConfidenceCard
                  key={row.matchConfidence}
                  row={row}
                  totalSent={totals.sent}
                />
              ))}
            </div>

            <h4 className="user-detail__card-title" style={{ marginTop: 24 }}>
              By pass
            </h4>
            <div className="admin-users__table-wrap">
              <table className="admin-users__table">
                <thead>
                  <tr>
                    <th>Pass</th>
                    <th>Sent</th>
                    <th>Delivered</th>
                    <th>Failed</th>
                    <th>Opened</th>
                    <th>Joined</th>
                    <th>Open rate</th>
                    <th>Join rate</th>
                  </tr>
                </thead>
                <tbody>
                  {perf.byKind.map((row) => (
                    <tr key={row.kind}>
                      <td>{KIND_LABEL[row.kind] ?? row.kind}</td>
                      <RateCells row={row} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h4 className="user-detail__card-title" style={{ marginTop: 24 }}>
              Top interest labels
            </h4>
            {perf.topLabels.length === 0 ? (
              <p className="user-detail__empty">
                No label carried a send in this window.
              </p>
            ) : (
              <div className="admin-users__table-wrap">
                <table className="admin-users__table">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Sent</th>
                      <th>Opened</th>
                      <th>Joined</th>
                      <th>Open rate</th>
                      <th>Join rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perf.topLabels.map((row) => (
                      <tr key={row.label}>
                        <td>{row.label}</td>
                        <td>{num(row.sent)}</td>
                        <td>{num(row.opened)}</td>
                        <td>{num(row.joined)}</td>
                        <td>{pct(row.openRate, row.sent)}</td>
                        <td>{pct(row.joinRate, row.sent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h4 className="user-detail__card-title" style={{ marginTop: 24 }}>
              Top matched activities
            </h4>
            {perf.topActivities.length === 0 ? (
              <p className="user-detail__empty">
                No send in this window matched on a tagged activity — every one
                of them came through the category bridge.
              </p>
            ) : (
              <div className="admin-users__table-wrap">
                <table className="admin-users__table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Category</th>
                      <th>Sent</th>
                      <th>Opened</th>
                      <th>Joined</th>
                      <th>Open rate</th>
                      <th>Join rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perf.topActivities.map((row) => (
                      <tr key={row.activityId}>
                        <td>{row.activityName || row.activityId}</td>
                        <td>{row.categoryName || '—'}</td>
                        <td>{num(row.sent)}</td>
                        <td>{num(row.opened)}</td>
                        <td>{num(row.joined)}</td>
                        <td>{pct(row.openRate, row.sent)}</td>
                        <td>{pct(row.joinRate, row.sent)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Section 2 — the participation log                                 */}
      {/* ---------------------------------------------------------------- */}
      <section className="user-detail__card user-detail__card--wide">
        <div className="event-detail__section-head">
          <h3 className="user-detail__card-title" style={{ margin: 0 }}>
            What people actually join
          </h3>
          <div className="admin-events__filters">
            <DaySelect
              label="Window"
              value={interestDays}
              options={INTEREST_WINDOWS}
              onChange={setInterestDays}
              ariaLabel="Participation window in days"
            />
          </div>
        </div>

        <p className="app-settings__hint">
          <strong>This is not the same measurement as the section above.</strong>{' '}
          Section 1 only covers events we chose to suggest, so it cannot tell you
          about a category we never suggest. This section counts what people
          joined regardless of how they found it — through search, a share link,
          a club, or a suggestion.
        </p>

        {interest.isLoading && (
          <div className="admin-users__loading">Loading participation data…</div>
        )}
        {interest.error != null && (
          <div className="admin-users__error">
            Could not load participation analytics.
          </div>
        )}

        {interestData != null && interestData.startedCollectingAt == null && (
          <div className="admin-tab-empty" style={{ padding: '40px 24px' }}>
            <h2 className="admin-tab-empty__title">Not yet collecting</h2>
            <p className="admin-tab-empty__sub">
              The participation log is empty — it holds no rows at all. Capture
              starts at the deploy that shipped this feature and runs from there
              forward; joins from before it are never backfilled, so this stays
              blank until the next join happens on a deployed build.
            </p>
          </div>
        )}

        {interestData != null &&
          interestData.startedCollectingAt != null &&
          interestData.totalParticipations === 0 && (
            <div className="admin-tab-empty" style={{ padding: '40px 24px' }}>
              <h2 className="admin-tab-empty__title">
                No data yet — collection started on{' '}
                {day(interestData.startedCollectingAt)}
              </h2>
              <p className="admin-tab-empty__sub">
                Nothing was joined in the last {interestDays} days. Collection
                began on {day(interestData.startedCollectingAt)} and only counts
                joins made after that, so a window that reaches back further than
                the start date still shows nothing from before it.
              </p>
            </div>
          )}

        {interestData != null && interestData.totalParticipations > 0 && (
          <>
            <div className="app-settings__grid">
              <StatTile
                label="Joins in window"
                value={num(interestData.totalParticipations)}
                sub={`last ${interestDays} days`}
              />
              <StatTile
                label="Collecting since"
                value={
                  interestData.startedCollectingAt != null
                    ? day(interestData.startedCollectingAt)
                    : '—'
                }
                sub="nothing before this date exists"
              />
              <StatTile
                label="Unmapped category joins"
                value={num(interestData.unmappedCategoryJoins)}
                sub={`${shareOf(
                  interestData.unmappedCategoryJoins,
                  interestData.totalParticipations,
                )} of all joins`}
              />
            </div>

            <p className="app-settings__hint" style={{ marginTop: 12 }}>
              Those{' '}
              <strong>{num(interestData.unmappedCategoryJoins)}</strong> join(s)
              landed in a category that maps to <strong>none</strong> of the ten
              known interest labels. That is the size of the blind spot, not a
              rounding error: nobody can declare an interest in those events, so
              the suggestion push can never reach them and no amount of tuning
              will change it. A large number here is an argument for widening the
              label set, not for raising a score floor.
            </p>

            <h4 className="user-detail__card-title" style={{ marginTop: 24 }}>
              Joins by interest label
            </h4>
            <p className="app-settings__hint">
              <strong>Declared vs attended.</strong> &ldquo;By users who declare
              it&rdquo; counts the joins made by people who list that label among
              their interests; the gap against total joins is the gap between
              what people say they like and what they actually turn up to.{' '}
              <strong>Caveat:</strong> the comparison is against each
              user&apos;s interests <em>as they are now</em>, not as they were at
              join time — someone who changed their interests last week is
              counted with today&apos;s list for joins made months ago.
            </p>
            {interestData.byLabel.length === 0 ? (
              <p className="user-detail__empty">
                No join in this window mapped to a known label.
              </p>
            ) : (
              <div className="admin-users__table-wrap">
                <table className="admin-users__table">
                  <thead>
                    <tr>
                      <th>Label</th>
                      <th>Joins</th>
                      <th>Share of joins</th>
                      <th>By users who declare it</th>
                      <th>Share of that label</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interestData.byLabel.map((row) => (
                      <tr key={row.label}>
                        <td>{row.label}</td>
                        <td>{num(row.joins)}</td>
                        <td>
                          {pct(row.share, interestData.totalParticipations)}
                        </td>
                        <td>{num(row.matchedDeclaredInterest)}</td>
                        <td>{pct(row.matchedShare, row.joins)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h4 className="user-detail__card-title" style={{ marginTop: 24 }}>
              Joins by taxonomy category
            </h4>
            {interestData.byTaxonomyCategory.length === 0 ? (
              <p className="user-detail__empty">
                No join in this window carried a taxonomy category.
              </p>
            ) : (
              <div className="admin-users__table-wrap">
                <table className="admin-users__table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Joins</th>
                      <th>Share of joins</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interestData.byTaxonomyCategory.map((row) => (
                      <tr key={row.categoryName}>
                        <td>{row.categoryName || '—'}</td>
                        <td>{num(row.joins)}</td>
                        <td>
                          {pct(row.share, interestData.totalParticipations)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h4 className="user-detail__card-title" style={{ marginTop: 24 }}>
              Joins by activity
            </h4>
            <p className="app-settings__hint">
              <strong>Not representative of the catalogue.</strong> Only{' '}
              {num(coverage?.rowsWithActivities ?? 0)} of{' '}
              {num(coverage?.participationRows ?? 0)} logged joins (
              {shareOf(
                coverage?.rowsWithActivities ?? 0,
                coverage?.participationRows ?? 0,
              )}
              ) carry any activity tags at all — ingested events carry none — so
              this table describes the tagged corner of the catalogue and nothing
              more. Read the relative order inside it; do not read it as what the
              app is used for.
            </p>
            {interestData.byActivity.length === 0 ? (
              <p className="user-detail__empty">
                No join in this window was on an event with tagged activities.
              </p>
            ) : (
              <div className="admin-users__table-wrap">
                <table className="admin-users__table">
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Category</th>
                      <th>Joins</th>
                      <th>Share of joins</th>
                    </tr>
                  </thead>
                  <tbody>
                    {interestData.byActivity.map((row) => (
                      <tr key={row.activityId}>
                        <td>{row.activityName || row.activityId}</td>
                        <td>{row.categoryName || '—'}</td>
                        <td>{num(row.joins)}</td>
                        <td>
                          {pct(row.share, interestData.totalParticipations)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {coverage != null && (
              <>
                <h4 className="user-detail__card-title" style={{ marginTop: 24 }}>
                  Coverage of the participation log
                </h4>
                <div className="app-settings__grid">
                  <StatTile
                    label="Rows logged"
                    value={num(coverage.participationRows)}
                    sub="every join we captured"
                  />
                  <StatTile
                    label="With an interest snapshot"
                    value={num(coverage.rowsWithSnapshot)}
                    sub={`${shareOf(
                      coverage.rowsWithSnapshot,
                      coverage.participationRows,
                    )} of rows`}
                  />
                  <StatTile
                    label="With activities"
                    value={num(coverage.rowsWithActivities)}
                    sub={`${shareOf(
                      coverage.rowsWithActivities,
                      coverage.participationRows,
                    )} of rows`}
                  />
                </div>
                <p className="app-settings__hint" style={{ marginTop: 12 }}>
                  Rows without an interest snapshot cannot contribute to the
                  declared-vs-attended comparison, and rows without activities
                  cannot appear in the activity table above. Both are expected to
                  be common early on and for ingested events forever, so treat
                  these three numbers as the denominator behind everything else
                  in this section.
                </p>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
};
